import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2Icon, CreditCardIcon, LockIcon, Loader2Icon, OctagonAlertIcon, ShieldCheckIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { TextInput } from '../../components/ui/TextInput';
import { SelectInput } from '../../components/ui/SelectInput';
import { Panel } from '../../components/ui/Panel';
import { Wordmark } from '../../components/Wordmark';
import { useAccount } from '../../contexts/AccountContext';
import { useAuth } from '../../contexts/AuthContext';
import { annualPrice } from '../../data/plans';
import { countries } from '../../data/alerts';
import { formatCurrency } from '../../utils/format';
type Stage = 'form' | 'processing' | 'declined' | 'done';
function formatCardNumber(value: string) {
  return value.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
}
function formatExpiry(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
}
export function Checkout() {
  const navigate = useNavigate();
  const {
    signIn
  } = useAuth();
  const location = useLocation();
  const draft = location.state as {
    domain?: string;
    wantSsl?: boolean;
    wantUptime?: boolean;
  } | null ?? {};
  const {
    plan,
    billingPeriod,
    setBillingPeriod,
    addMonitor,
    markSubscribed,
    defaults,
    recipients
  } = useAccount();
  const [stage, setStage] = useState<Stage>('form');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [name, setName] = useState('');
  const [country, setCountry] = useState(countries[0]);
  const [promo, setPromo] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const subtotal = billingPeriod === 'annual' ? annualPrice(plan) : plan.monthlyPrice;
  const discount = promoApplied ? subtotal * 0.1 : 0;
  const tax = (subtotal - discount) * 0.18;
  const total = subtotal - discount + tax;
  function applyPromo() {
    if (promo.trim().toUpperCase() === 'CERTWATCH10') {
      setPromoApplied(true);
      setPromoError(null);
      return;
    }
    setPromoApplied(false);
    setPromoError('That code is not recognised.');
  }
  function pay(event: React.FormEvent) {
    event.preventDefault();
    const next: Record<string, string> = {};
    const digits = cardNumber.replace(/\s/g, '');
    if (digits.length !== 16) {
      next.cardNumber = 'A card number is 16 digits.';
    }
    if (!/^\d{2}\/\d{2}$/.test(expiry)) {
      next.expiry = 'Use MM/YY.';
    }
    if (cvc.length < 3) {
      next.cvc = '3 or 4 digits.';
    }
    if (name.trim().length < 2) {
      next.name = 'Enter the name on the card.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      return;
    }
    setStage('processing');
    window.setTimeout(() => {
      if (digits.endsWith('0002')) {
        setStage('declined');
        return;
      }
      if (draft.domain) {
        addMonitor({
          domain: draft.domain,
          label: 'First site',
          sslEnabled: draft.wantSsl ?? true,
          uptimeEnabled: draft.wantUptime ?? true,
          warningWindowDays: plan.warningWindowFixed ?? defaults.warningWindowDays,
          reminderCadence: defaults.reminderCadence,
          pingIntervalMinutes: plan.pingOptions[plan.pingOptions.length - 1],
          method: 'GET',
          expectedStatus: 200,
          timeoutSeconds: 10,
          failureThreshold: defaults.failureThreshold,
          recipientIds: recipients.slice(0, 1).map(r => r.id)
        });
      }
      markSubscribed();
      setStage('done');
    }, 1400);
  }
  const summary = <Panel className="lg:sticky lg:top-8" data-id="element-1484" data-mp="2srajztw0xng4">
      <div className="border-b border-line px-5 py-4" data-id="element-1485" data-mp="2spfwc7tdbh8c">
        <h2 className="text-sm font-semibold text-ink" data-id="element-1486" data-mp="2xqvg7wjt7hp5">Order summary</h2>
      </div>
      <div className="px-5 py-4" data-id="element-1487" data-mp="2jrdqneyow0eq">
        <div className="flex items-start justify-between gap-4" data-id="element-1488" data-mp="2jqcd93u7b1tu">
          <div data-id="element-1489" data-mp="2opfh4kyg3208">
            <p className="text-sm font-medium text-ink" data-id="element-1490" data-mp="2oow1xc06irpw">{plan.name} plan</p>
            <p className="tabular mt-0.5 text-[13px] text-ink-muted" data-id="element-1491" data-mp="2a0j2u65sosr6">
              {plan.services} services included
            </p>
          </div>
          <span className="tabular text-sm font-medium text-ink" data-id="element-1492" data-mp="2et2egxkx9dtc">
            {formatCurrency(subtotal)}
          </span>
        </div>

        <div className="mt-4 inline-flex items-center gap-1 rounded-control border border-line-strong bg-canvas p-1" data-id="element-1493" data-mp="251y3e8ydad3s">
          {(['monthly', 'annual'] as const).map(option => <button key={option} type="button" onClick={() => setBillingPeriod(option)} aria-pressed={billingPeriod === option} className={`rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors duration-150 ease-snap ${billingPeriod === option ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink'}`} data-id="element-1494" data-mp="1r4j7cldlz72u">
              {option === 'monthly' ? 'Monthly' : 'Yearly · 2 free'}
            </button>)}
        </div>

        <dl className="mt-5 space-y-2.5 border-t border-line pt-4 text-[13px]" data-id="element-1495" data-mp="1vc9caooftero">
          <div className="flex justify-between gap-4" data-id="element-1496" data-mp="1qezb6broreo0">
            <dt className="text-ink-muted" data-id="element-1497" data-mp="254ounn3nc5d3">
              Billed {billingPeriod === 'annual' ? 'yearly' : 'monthly'}
            </dt>
            <dd className="tabular text-ink" data-id="element-1498" data-mp="205kkou8we3lp">{formatCurrency(subtotal)}</dd>
          </div>
          {promoApplied && <div className="flex justify-between gap-4" data-id="element-1499" data-mp="2e2r1wkdz3u8t">
              <dt className="text-healthy-ink" data-id="element-1500" data-mp="2a49ppmfp3tkj">CERTWATCH10 · 10% off</dt>
              <dd className="tabular text-healthy-ink" data-id="element-1501" data-mp="2h6olvqz5dofa">
                −{formatCurrency(discount)}
              </dd>
            </div>}
          <div className="flex justify-between gap-4" data-id="element-1502" data-mp="2m618cam3wthc">
            <dt className="text-ink-muted" data-id="element-1503" data-mp="2r42rswng2utc">Tax (18%)</dt>
            <dd className="tabular text-ink" data-id="element-1504" data-mp="2r2jbj61sgjrk">{formatCurrency(tax)}</dd>
          </div>
        </dl>

        <div className="mt-4 flex items-baseline justify-between gap-4 border-t border-line pt-4" data-id="element-1505" data-mp="2vxfgf6s4j1m3">
          <span className="text-sm font-semibold text-ink" data-id="element-1506" data-mp="2zz0qkkd9x1sl">Total today</span>
          <span className="tabular text-xl font-semibold text-ink" data-id="element-1507" data-mp="34ug0j185udjq">
            {formatCurrency(total)}
          </span>
        </div>

        <div className="mt-4 flex gap-2" data-id="element-1508" data-mp="39t9hx4qkiop6">
          <label htmlFor="promo" className="sr-only" data-id="element-1509" data-mp="3et5kczjma82m">
            Promo code
          </label>
          <TextInput id="promo" placeholder="Promo code" value={promo} className="h-9 text-[13px]" onChange={event => setPromo(event.target.value)} data-id="element-1510" data-mp="1fhur0kltmj7u" />
          <Button variant="secondary" size="sm" onClick={applyPromo} data-id="element-1511" data-mp="1mmbckg762sv">
            Apply
          </Button>
        </div>
        {promoError && <p className="mt-1.5 text-xs text-critical-ink" data-id="element-1512" data-mp="3oy596k4rr38b">{promoError}</p>}

        <p className="mt-4 flex items-start gap-2 border-t border-line pt-4 text-xs leading-relaxed text-ink-soft" data-id="element-1513" data-mp="3ovrck6ufxiyn">
          <ShieldCheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1514" data-mp="kjniobx84ezz" />
          Monitoring starts immediately. Cancel from Billing at any time and it
          runs to the end of the period you paid for.
        </p>
      </div>
    </Panel>;
  if (stage === 'done') {
    return <div className="flex min-h-full w-full items-center justify-center bg-canvas px-5 py-16" data-id="element-1515" data-mp="6j31fk6yir8l">
        <Panel className="w-full max-w-md p-8 text-center" data-id="element-1516" data-mp="2wahnt2voc8km">
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-healthy-soft" data-id="element-1517" data-mp="2rad8hg6i5jvz">
            <CheckCircle2Icon className="h-5 w-5 text-healthy" aria-hidden="true" data-id="element-1518" data-mp="v7ibq1fdtyxz" />
          </span>
          <h1 className="mt-5 text-xl font-semibold text-ink" data-id="element-1519" data-mp="1073bge2dmbbr">
            You are subscribed to {plan.name}
          </h1>
          <p className="mt-2.5 text-sm leading-relaxed text-ink-muted" data-id="element-1520" data-mp="lb5i13ju7l38">
            {formatCurrency(total)} charged. {plan.services} services are
            available, and {draft.domain ?? 'your first domain'} is queued for
            its first check now.
          </p>
          <div className="mt-6" data-id="element-1521" data-mp="1ivi0ivt4vf9x">
            <Button size="lg" className="w-full" onClick={() => {
            signIn('user', 'user123');
            navigate('/app');
          }} data-id="element-1522" data-mp="1iynog7q10b42">
              Go to my monitors
            </Button>
          </div>
          <p className="mt-3 text-xs text-ink-soft" data-id="element-1523" data-mp="1wv9kdd6bzpvu">
            A receipt is on its way to your inbox.
          </p>
        </Panel>
      </div>;
  }
  return <div className="min-h-full w-full bg-canvas" data-id="element-1524" data-mp="3mco9lnjgexjn">
      <header className="border-b border-line bg-surface" data-id="element-1525" data-mp="3mbd5fmm3jns0">
        <div className="mx-auto flex h-16 w-full max-w-[1080px] items-center justify-between gap-4 px-5 sm:px-8" data-id="element-1526" data-mp="3hdtryox83f14">
          <Link to="/" className="flex items-center gap-2" data-id="element-1527" data-mp="3w2zmhzl48h2o">
            <Wordmark size="sm" data-id="element-1528" data-mp="3r46akblh8y4m" />
          </Link>
          <span className="flex items-center gap-1.5 text-xs text-ink-soft" data-id="element-1529" data-mp="3r6j1vfd4kkuc">
            <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1530" data-mp="33fdn73ix7ylm" />
            Secure checkout
          </span>
        </div>
      </header>

      <div className="mx-auto grid w-full max-w-[1080px] gap-8 px-5 py-10 sm:px-8 lg:grid-cols-[1.25fr_0.9fr] lg:py-14" data-id="element-1531" data-mp="3cfjwsebi0efi">
        <div data-id="element-1532" data-mp="38df4biwsdnug">
          <h1 className="text-[1.7rem] font-semibold leading-tight tracking-tight text-ink" data-id="element-1533" data-mp="38cvms3b1t1gy">
            Confirm your subscription
          </h1>
          <p className="mt-2 text-[15px] text-ink-muted" data-id="element-1534" data-mp="rrdzdv86t9q6">
            One charge, then monitoring runs on its own.
          </p>

          {stage === 'declined' && <div role="alert" className="mt-6 flex items-start gap-3 rounded-card border border-critical-border bg-critical-soft px-4 py-3.5" data-id="element-1535" data-mp="2ggq6rggpctbp">
              <OctagonAlertIcon className="mt-0.5 h-4 w-4 shrink-0 text-critical" aria-hidden="true" data-id="element-1536" data-mp="27oo6pjajpf1y" />
              <div data-id="element-1537" data-mp="22oscucvkb404">
                <p className="text-[13px] font-medium text-critical-ink" data-id="element-1538" data-mp="1xra4anlgiqsn">
                  Your bank declined the card
                </p>
                <p className="mt-1 text-[13px] leading-relaxed text-critical-ink/85" data-id="element-1539" data-mp="1srnrgf9jjckd">
                  Nothing has been charged. Try another card, or contact your
                  bank — they can usually approve it on a second attempt.
                </p>
              </div>
            </div>}

          <form onSubmit={pay} className="mt-6 space-y-5" noValidate data-id="element-1540" data-mp="3j4cu2brpog3a">
            <Panel className="p-5" data-id="element-1541" data-mp="3e5iedip1j7ne">
              <div className="flex items-center justify-between gap-3" data-id="element-1542" data-mp="3e6jnviqerm30">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-ink" data-id="element-1543" data-mp="3lo9khvcgvrv1">
                  <CreditCardIcon className="h-4 w-4 text-ink-muted" aria-hidden="true" data-id="element-1544" data-mp="3qnm323wb2crt" />
                  Card details
                </h2>
                <span className="text-xs text-ink-soft" data-id="element-1545" data-mp="492qmcqw57nz">
                  Handled by our payment gateway
                </span>
              </div>

              <div className="mt-4 space-y-4" data-id="element-1546" data-mp="32sjgy3ovynf1">
                <Field label="Card number" htmlFor="card-number" error={errors.cardNumber} hint="4242 4242 4242 4242 approves · any card ending 0002 declines." data-id="element-1547" data-mp="37oqcd2jnogiq">
                  <TextInput id="card-number" inputMode="numeric" autoComplete="cc-number" placeholder="4242 4242 4242 4242" className="tabular" value={cardNumber} invalid={Boolean(errors.cardNumber)} onChange={event => setCardNumber(formatCardNumber(event.target.value))} data-id="element-1548" data-mp="3go3ok2z5zj75" />
                </Field>

                <div className="grid gap-4 sm:grid-cols-2" data-id="element-1549" data-mp="2x2ge93rq4hj4">
                  <Field label="Expiry" htmlFor="card-expiry" error={errors.expiry} data-id="element-1550" data-mp="2x2zwkllad7xo">
                    <TextInput id="card-expiry" inputMode="numeric" autoComplete="cc-exp" placeholder="MM/YY" className="tabular" value={expiry} invalid={Boolean(errors.expiry)} onChange={event => setExpiry(formatExpiry(event.target.value))} data-id="element-1551" data-mp="29d5m2aofvvgl" />
                  </Field>
                  <Field label="CVC" htmlFor="card-cvc" error={errors.cvc} data-id="element-1552" data-mp="1lmrsgfq7hojw">
                    <TextInput id="card-cvc" inputMode="numeric" autoComplete="cc-csc" placeholder="123" className="tabular" value={cvc} invalid={Boolean(errors.cvc)} onChange={event => setCvc(event.target.value.replace(/\D/g, '').slice(0, 4))} data-id="element-1553" data-mp="147hqv40pjae" />
                  </Field>
                </div>
              </div>
            </Panel>

            <Panel className="p-5" data-id="element-1554" data-mp="avln3jinhdbg">
              <h2 className="text-sm font-semibold text-ink" data-id="element-1555" data-mp="auanlvw8mrq5">Billing details</h2>
              <div className="mt-4 space-y-4" data-id="element-1556" data-mp="fslubxr8km45">
                <Field label="Name on card" htmlFor="card-name" error={errors.name} data-id="element-1557" data-mp="2vl19m2au6fva">
                  <TextInput id="card-name" autoComplete="cc-name" value={name} invalid={Boolean(errors.name)} onChange={event => setName(event.target.value)} data-id="element-1558" data-mp="2vn4axihfvvfb" />
                </Field>
                <Field label="Billing country" htmlFor="card-country" data-id="element-1559" data-mp="3b1802k9jkno5">
                  <SelectInput id="card-country" value={country} onChange={event => setCountry(event.target.value)} options={countries.map(item => ({
                  value: item,
                  label: item
                }))} data-id="element-1560" data-mp="371z4d9uxyfpy" />
                </Field>
              </div>
            </Panel>

            <Button type="submit" size="lg" className="w-full" disabled={stage === 'processing'} data-id="element-1561" data-mp="3pq47cf3rxwnp">
              {stage === 'processing' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1562" data-mp="2eafhnfz4yjj7" />}
              {stage === 'processing' ? 'Contacting your bank' : `Pay ${formatCurrency(total)} and start monitoring`}
            </Button>
          </form>
        </div>

        {summary}
      </div>
    </div>;
}